import { useState } from 'react';

interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CARE_NEEDS = [
  'Personal Care',
  'Companion Care',
  "Alzheimer's & Dementia Care",
  'Respite Care',
  'Post-Hospital Recovery',
  'Not Sure Yet',
];

const TIMELINES = [
  'Immediately',
  'Within 1 Week',
  'Within 1 Month',
  'Just Exploring',
];

export default function ConsultationModal({ isOpen, onClose }: ConsultationModalProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({ name: '', care_needs: '', timeline: '', phone: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!formData.phone.trim()) return;
    setLoading(true);
    try {
      const body = new URLSearchParams({
        name: formData.name,
        care_needs: formData.care_needs,
        timeline: formData.timeline,
        phone: formData.phone,
      });
      await fetch('https://readdy.ai/api/form/d6va47u7ipaugi04lhug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStep(1);
      setFormData({ name: '', care_needs: '', timeline: '', phone: '' });
      setSubmitted(false);
    }, 300);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{ backgroundColor: 'rgba(15,42,68,0.75)' }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        data-readdy-form
        className="bg-white rounded-2xl w-full max-w-lg overflow-hidden"
        style={{ boxShadow: '0 20px 60px rgba(15,42,68,0.3)' }}
      >
        {/* Header */}
        <div className="bg-navy px-8 py-6 flex items-center justify-between">
          <div>
            <p className="text-gold text-xs font-semibold uppercase tracking-widest mb-1">Free Consultation</p>
            <h3 className="font-heading text-white text-xl font-semibold">Schedule Your Care Consultation</h3>
          </div>
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Progress */}
        {!submitted && (
          <div className="px-8 pt-5 flex items-center gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                  s <= step ? 'bg-gold' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        )}

        <div className="px-8 py-6">
          {submitted ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 flex items-center justify-center bg-sage/20 rounded-full mx-auto mb-4">
                <i className="ri-check-line text-3xl text-sage"></i>
              </div>
              <h4 className="font-heading text-navy text-2xl font-semibold mb-2">Thank You!</h4>
              <p className="text-gray-500 text-sm leading-relaxed">
                We&apos;ve received your consultation request. Our care team will reach out within 2 hours to discuss your loved one&apos;s needs.
              </p>
              <button
                onClick={handleClose}
                className="whitespace-nowrap mt-6 bg-navy text-white font-semibold px-8 py-3 rounded-full hover:bg-navy/90 transition-colors cursor-pointer"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              {/* Step 1: Name */}
              {step === 1 && (
                <div>
                  <p className="text-xs text-sage font-semibold uppercase tracking-widest mb-1">Step 1 of 4</p>
                  <h4 className="font-heading text-navy text-xl font-semibold mb-4">What&apos;s your name?</h4>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-navy placeholder-gray-400 focus:outline-none focus:border-sage transition-colors"
                  />
                  <button
                    onClick={() => formData.name.trim() && setStep(2)}
                    disabled={!formData.name.trim()}
                    className="whitespace-nowrap mt-4 w-full bg-navy text-white font-semibold py-3 rounded-full disabled:opacity-40 hover:bg-navy/90 transition-colors cursor-pointer"
                  >
                    Continue &rarr;
                  </button>
                </div>
              )}

              {/* Step 2: Care Needs */}
              {step === 2 && (
                <div>
                  <p className="text-xs text-sage font-semibold uppercase tracking-widest mb-1">Step 2 of 4</p>
                  <h4 className="font-heading text-navy text-xl font-semibold mb-4">What type of care is needed?</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {CARE_NEEDS.map((need) => (
                      <button
                        key={need}
                        onClick={() => { setFormData({ ...formData, care_needs: need }); setStep(3); }}
                        className={`whitespace-normal text-left px-4 py-3 rounded-lg text-sm font-medium border transition-all cursor-pointer ${
                          formData.care_needs === need
                            ? 'bg-navy text-white border-navy'
                            : 'bg-offWhite text-navy border-gray-200 hover:border-sage'
                        }`}
                      >
                        {need}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3: Timeline */}
              {step === 3 && (
                <div>
                  <p className="text-xs text-sage font-semibold uppercase tracking-widest mb-1">Step 3 of 4</p>
                  <h4 className="font-heading text-navy text-xl font-semibold mb-4">When do you need care to start?</h4>
                  <div className="flex flex-col gap-2">
                    {TIMELINES.map((t) => (
                      <button
                        key={t}
                        onClick={() => { setFormData({ ...formData, timeline: t }); setStep(4); }}
                        className={`whitespace-nowrap text-left px-5 py-3 rounded-lg text-sm font-medium border transition-all cursor-pointer ${
                          formData.timeline === t
                            ? 'bg-navy text-white border-navy'
                            : 'bg-offWhite text-navy border-gray-200 hover:border-sage'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 4: Phone */}
              {step === 4 && (
                <div>
                  <p className="text-xs text-sage font-semibold uppercase tracking-widest mb-1">Step 4 of 4</p>
                  <h4 className="font-heading text-navy text-xl font-semibold mb-1">Best number to reach you?</h4>
                  <p className="text-gray-400 text-xs mb-4">Our care team will call within 2 hours.</p>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="(xxx) xxx-xxxx"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-navy placeholder-gray-400 focus:outline-none focus:border-sage transition-colors"
                  />
                  <button
                    onClick={handleSubmit}
                    disabled={!formData.phone.trim() || loading}
                    className="whitespace-nowrap mt-4 w-full bg-gold text-navy font-bold py-3 rounded-full disabled:opacity-40 hover:brightness-90 transition-all cursor-pointer"
                  >
                    {loading ? 'Submitting...' : 'Get My Free Consultation'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
